import React, { memo, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import Card from 'transfix-ui/components/Card'
import { Box, Divider, Grid, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { BidResponse } from 'apps/fleet-planner/types'
import { BidAction } from './BidAction'
import { BidBy } from './BidBy'
import { Bids } from './Bids'
import { CounterOfferDetails } from './CounterOfferDetails'
import { Location } from './Location'

const useBidsCardRowStyles = makeStyles(theme => ({
  card: {
    borderColor: brandColors.coolGray4,
  },
  divider: {
    color: brandColors.coolGray1,
  },
  counterOfferRow: {
    borderTop: 'none',
    backgroundColor: brandColors.skyBlue0,
    padding: theme.spacing(2),
  },
}))

interface IBidsCardRow {
  bid: BidResponse
  onClick: () => void
}

export const BidsCardRow = memo<IBidsCardRow>(({ bid, onClick }) => {
  const classes = useBidsCardRowStyles()
  const history = useHistory()
  const { trackEvent } = useFMSAnalytics()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: fmsCounterBiddingEnabled } = getFlag('fmsCounterBidding')
  const { setActiveShipmentDetailsId } = useMarketplaceContext()
  const { counterBidAmount, shipment, status: bidStatus } = bid || {}
  const { id, state: shipmentStatus } = shipment || {}
  const loadUnavailableCardRow = bidStatus === 'declined' && shipmentStatus !== 'pending'
  const rowClickHandler = () => {
    onClick()
    if (!loadUnavailableCardRow) {
      history.push(`/bids/${id}`)
      setActiveShipmentDetailsId(id)
    }
  }
  const counterOfferDetailsOnClick = () => {
    onClick()
    trackEvent('Bids Page', 'PENDING_CLICKED_ACCEPT_COUNTER_OFFER', { bid })
  }
  const offerCounterBid = useMemo(
    () => fmsCounterBiddingEnabled && counterBidAmount && bidStatus === 'countered',
    [counterBidAmount, bidStatus, fmsCounterBiddingEnabled]
  )
  return (
    <Box py={1.5}>
      <Card noPadding className={classes.card}>
        <Box onClick={rowClickHandler} data-test='bid-card-row' p={2}>
          <Grid container alignItems='center'>
            <Grid item xs={8}>
              <div>
                <Bids bid={bid} />
              </div>
            </Grid>
            <Grid item xs={4}>
              <BidAction onClick={onClick} bid={bid} />
            </Grid>
          </Grid>
          <Box py={2}>
            <BidBy bid={bid} />
          </Box>
          <Divider className={classes.divider} />
          <Box pt={2}>
            <Location bid={bid} />
          </Box>
        </Box>
        {offerCounterBid && (
          <Box className={classes.counterOfferRow}>
            <CounterOfferDetails
              compact
              bid={bid}
              shipmentId={bid.shipment?.id}
              onClick={counterOfferDetailsOnClick}
            />
          </Box>
        )}
      </Card>
    </Box>
  )
})
